import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faExclamationTriangle,
  faClipboardCheck,
  faDesktop,
  faSpinner,
  faCalendarAlt,
  faSync,
  faFilter,
  faChevronLeft,
  faChevronRight,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import ErrorModal from '../../components/ErrorModal';
import SearchableSelect from '../../components/SearchableSelect';

const CatiAnomalies = () => {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState('');
  const [surveys, setSurveys] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [globalStats, setGlobalStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);
  const [showPostesModal, setShowPostesModal] = useState(false);
  const [selectedPostesType, setSelectedPostesType] = useState('');
  const [postesList, setPostesList] = useState([]);
  const [postesLoading, setPostesLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [resolvedFilter, setResolvedFilter] = useState('false'); // 'false' = unresolved, 'true' = resolved, '' = all
  const [showResolveModal, setShowResolveModal] = useState(false);
  // Carries an editable field: only Escape and the buttons dismiss it.
  const resolveDismiss = useModalDismiss(() => setShowResolveModal(false), { backdropClose: false, enabled: showResolveModal });
  const [resolvingAnomaly, setResolvingAnomaly] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [resolving, setResolving] = useState(false);

  // Surveys list — fetch once
  useEffect(() => {
    fetchSurveys();
  }, []);

  // Stats — refetch when content filters change (not resolved toggle or page)
  useEffect(() => {
    fetchGlobalStats();
  }, [searchTerm, dateFrom, dateTo, selectedType, selectedSurvey]);

  // Anomalies list — refetch on any filter/page change
  useEffect(() => {
    fetchAnomalies();
  }, [currentPage, searchTerm, dateFrom, dateTo, selectedType, selectedSurvey, resolvedFilter]);

  const fetchAnomalies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        search: searchTerm,
        date_from: dateFrom,
        date_to: dateTo,
        type: selectedType,
        survey_id: selectedSurvey
      });
      if (resolvedFilter !== '') {
        params.set('resolved', resolvedFilter);
      }

      const response = await api.get(`/cati/anomalies?${params}`);
      
      if (response.data.success) {
        // Handle the new nested data structure
        const anomaliesData = response.data.data.data || response.data.data || [];
        setAnomalies(anomaliesData);
        setPagination(response.data.data.pagination || {});
      }
    } catch (error) {
      console.error('Error fetching anomalies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveys = async () => {
    try {
      const response = await api.get('/cati/surveys');
      if (response.data.success) {
        // Convert object to array if needed
        const surveysData = response.data.data || [];
        const surveysArray = Array.isArray(surveysData) ? surveysData : Object.values(surveysData);
        setSurveys(surveysArray);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const fetchGlobalStats = async () => {
    try {
      setStatsLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        date_from: dateFrom,
        date_to: dateTo,
        type: selectedType,
        survey_id: selectedSurvey
      });

      const response = await api.get(`/cati/anomalies/stats?${params}`);
      
      console.log('Global stats response:', response.data);
      
      if (response.data.success) {
        setGlobalStats(response.data.data);
        console.log('Global stats set:', response.data.data);
      } else {
        console.log('Global stats API error:', response.data);
        setGlobalStats({});
      }
    } catch (error) {
      console.error('Error fetching global stats:', error);
      setGlobalStats({});
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchPostesByType = async (type) => {
    try {
      setPostesLoading(true);
      const params = new URLSearchParams({
        type: type,
        search: searchTerm,
        date_from: dateFrom,
        date_to: dateTo,
        survey_id: selectedSurvey
      });

      const response = await api.get(`/cati/anomalies/postes?${params}`);
      
      if (response.data.success) {
        setPostesList(response.data.data.postes);
        setSelectedPostesType(response.data.data.type);
        setShowPostesModal(true);
      }
    } catch (error) {
      console.error('Error fetching postes:', error);
      setErrorMessage('Erreur lors du chargement des postes');
      setShowErrorModal(true);
    } finally {
      setPostesLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'survey':
        setSelectedSurvey(value);
        break;
      case 'type':
        setSelectedType(value);
        break;
      case 'dateFrom':
        setDateFrom(value);
        break;
      case 'dateTo':
        setDateTo(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await api.post('/cati/anomalies/refresh');
      
      if (response.data.success) {
        // Refresh both anomalies data and global stats
        await fetchAnomalies();
        await fetchGlobalStats();
        setSuccessMessage(response.data.message || 'Anomalies mises à jour avec succès');
        setShowSuccessModal(true);
      } else {
        setErrorMessage('Erreur lors de la mise à jour des anomalies');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error refreshing anomalies:', error);
      setErrorMessage(error.response?.data?.message || 'Erreur lors de la mise à jour des anomalies');
      setShowErrorModal(true);
    } finally {
      setRefreshing(false);
    }
  };

  const handleResolve = async () => {
    if (!resolvingAnomaly) return;
    setResolving(true);
    try {
      const response = await api.post(`/cati/anomalies/${resolvingAnomaly.id}/resolve`, {
        resolution_note: resolutionNote || null,
      });
      if (response.data.success) {
        setShowResolveModal(false);
        setResolvingAnomaly(null);
        setResolutionNote('');
        setSuccessMessage('Anomalie résolue avec succès');
        setShowSuccessModal(true);
        fetchAnomalies();
        fetchGlobalStats();
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Erreur lors de la résolution');
      setShowErrorModal(true);
    } finally {
      setResolving(false);
    }
  };

  const openResolveModal = (anomaly) => {
    setResolvingAnomaly(anomaly);
    setResolutionNote('');
    setShowResolveModal(true);
  };

  const handlePostNotExistsClick = () => {
    fetchPostesByType('post_not_found');
  };

  const handlePostUnassignedClick = () => {
    fetchPostesByType('post_unassigned');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle both ISO date strings and date objects
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ok':
        return 'bg-green-100 text-green-800';
      case 'missing_poste':
        return 'bg-red-100 text-red-800';
      case 'unknown_poste':
        return 'bg-yellow-100 text-yellow-800';
      case 'unassigned_poste':
        return 'bg-orange-100 text-orange-800';
      case 'ambiguous_overlap':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ok':
        return 'OK';
      case 'missing_poste':
        return 'POSTE Manquant';
      case 'unknown_poste':
        return 'POSTE Inconnu';
      case 'unassigned_poste':
        return 'POSTE Non Assigné';
      case 'ambiguous_overlap':
        return 'Chevauchement';
      default:
        return status;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'post_not_found':
        return 'bg-red-100 text-red-800';
      case 'post_unassigned':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'post_not_found':
        return 'POSTE Inexistant';
      case 'post_unassigned':
        return 'POSTE Non Assigné';
      default:
        return type;
    }
  };



  // Use global stats instead of calculating from current page
  const counts = {
    total: statsLoading ? '...' : (globalStats.total || 0),
    post_not_found: statsLoading ? '...' : (globalStats.post_not_found || 0),
    post_unassigned: statsLoading ? '...' : (globalStats.post_unassigned || 0),
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">ANOMALIES CATI</h1>
                <p className="text-gray-600">Détection et analyse des anomalies dans les soumissions LimeSurvey</p>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FontAwesomeIcon 
                  icon={refreshing ? faSpinner : faSync} 
                  className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} 
                />
                {refreshing ? 'Actualisation...' : 'Actualiser'}
              </button>
            </div>
          </div>

          {/* Resolved/Unresolved Toggle */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-red-600 rounded-lg">
              <FontAwesomeIcon icon={faFilter} className="text-white" />
            </div>
            {[
              { value: 'false', label: 'Non Résolues' },
              { value: 'true', label: 'Résolues' },
              { value: '', label: 'Toutes' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setResolvedFilter(opt.value); setCurrentPage(1); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  resolvedFilter === opt.value
                    ? 'bg-red-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr_1fr_1fr_1fr] gap-4 items-end">
              <div className="relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 pr-4 h-[42px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 w-full"
                />
              </div>
              <div>
                <SearchableSelect
                  options={[{ id: '', title: 'Toutes les enquêtes' }, ...surveys.filter(s => s)]}
                  selectedValue={selectedSurvey || ''}
                  onChange={(val) => handleFilterChange('survey', val || '')}
                  placeholder="Toutes les enquêtes"
                  searchPlaceholder="Rechercher une enquête..."
                  triggerClassName="px-4 h-[42px] flex items-center"
                  showOptionMeta={false}
                  getOptionLabel={(o) => o.title}
                />
              </div>
              <div>
                <select
                  value={selectedType}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full px-4 h-[42px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Tous les types</option>
                  <option value="post_not_found">POSTE Inexistant</option>
                  <option value="post_unassigned">POSTE Non Assigné</option>
                </select>
              </div>
              <div>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-4 h-[42px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Date de début"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-4 h-[42px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Date de fin"
                />
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
             <div 
               className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
               onClick={handlePostNotExistsClick}
               title="Cliquer pour voir la liste des postes inexistants"
             >
               <div className="flex items-center">
                 <div className="p-3 bg-red-100 rounded-full">
                   <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-xl" />
                 </div>
                 <div className="ml-4">
                   <p className="text-sm font-medium text-gray-600">POSTE Inexistant</p>
                   <p className="text-2xl font-bold text-gray-900">{counts.post_not_found}</p>
                 </div>
               </div>
             </div>
             <div 
               className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
               onClick={handlePostUnassignedClick}
               title="Cliquer pour voir la liste des postes non assignés"
             >
               <div className="flex items-center">
                 <div className="p-3 bg-yellow-100 rounded-full">
                   <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 text-xl" />
                 </div>
                 <div className="ml-4">
                   <p className="text-sm font-medium text-gray-600">POSTE Non Assigné</p>
                   <p className="text-2xl font-bold text-gray-900">{counts.post_unassigned}</p>
                 </div>
               </div>
             </div>
          </div>

          {/* Anomalies Table */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
                <p className="text-gray-600">Chargement des anomalies...</p>
              </div>
            ) : anomalies.length === 0 ? (
              <div className="p-8 text-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-gray-400 mb-4" />
                <p className="text-gray-600">Aucune anomalie trouvée</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Enquête
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          POSTE
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {anomalies.map((anomaly) => (
                        <React.Fragment key={anomaly.id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {anomaly.total_count || 1}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {anomaly.survey_title || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {anomaly.poste_number || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatDate(anomaly.response_date)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(anomaly.type)}`}>
                                {getTypeText(anomaly.type)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {anomaly.description || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {anomaly.resolved ? (
                                <div>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Résolue
                                  </span>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {anomaly.resolved_by_name && <span>Par {anomaly.resolved_by_name}</span>}
                                    {anomaly.resolved_at && <span className="ml-1">le {anomaly.resolved_at}</span>}
                                  </div>
                                  {anomaly.resolution_note && (
                                    <div className="text-xs text-gray-600 mt-1 italic max-w-[200px] truncate" title={anomaly.resolution_note}>
                                      {anomaly.resolution_note}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Non résolue
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {!anomaly.resolved && (
                                <button
                                  onClick={() => openResolveModal(anomaly)}
                                  className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                                >
                                  Résoudre
                                </button>
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                  <div className="bg-white px-4 sm:px-6 py-4 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      {/* Mobile: Show page info */}
                      <div className="sm:hidden text-sm text-gray-700 text-center w-full">
                        Page {currentPage} sur {pagination.last_page}
                      </div>
                      
                      {/* Desktop: Show full info */}
                      <div className="hidden sm:block text-sm text-gray-700">
                        <span>
                          Affichage de <span className="font-medium">{pagination.from || 0}</span> à{' '}
                          <span className="font-medium">{pagination.to || 0}</span> sur{' '}
                          <span className="font-medium">{pagination.total || 0}</span> résultats
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 w-full sm:w-auto justify-center sm:justify-end">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                        </button>
                        
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                            let pageNum;
                            if (pagination.last_page <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= pagination.last_page - 2) {
                              pageNum = pagination.last_page - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
                                  currentPage === pageNum
                                    ? 'bg-red-600 text-white border-red-600'
                                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === pagination.last_page}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Postes List Modal */}
      {showPostesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedPostesType === 'post_not_found' ? 'Postes Inexistants' : 'Postes Non Assignés'}
                </h3>
                <button
                  onClick={() => setShowPostesModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xl" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {postesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <FontAwesomeIcon icon={faSpinner} className="text-2xl text-red-600 animate-spin mr-3" />
                  <span className="text-gray-600">Chargement des postes...</span>
                </div>
              ) : postesList.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    {postesList.length} poste{postesList.length > 1 ? 's' : ''} trouvé{postesList.length > 1 ? 's' : ''}:
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {postesList.map((poste, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-center font-mono text-sm font-medium text-gray-700"
                      >
                        {poste}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-gray-400 mb-4" />
                  <p className="text-gray-600">Aucun poste trouvé</p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowPostesModal(false)}
                className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && resolvingAnomaly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...resolveDismiss}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-green-200 bg-green-50 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faClipboardCheck} className="text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Résoudre l'anomalie</h3>
                  <p className="text-sm text-gray-600">Poste {resolvingAnomaly.poste_number} — {resolvingAnomaly.survey_title}</p>
                </div>
              </div>
              <button onClick={() => setShowResolveModal(false)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="text-sm text-gray-700">{resolvingAnomaly.description}</div>
                <div className="text-xs text-gray-500 mt-1">Date : {formatDate(resolvingAnomaly.response_date)}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note de résolution <span className="text-gray-400">(optionnel)</span>
                </label>
                <textarea
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows="3"
                  placeholder="Décrivez comment l'anomalie a été traitée..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowResolveModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleResolve}
                disabled={resolving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
              >
                {resolving ? (
                  <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />Résolution...</>
                ) : (
                  'Marquer comme résolue'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
        duration={3000}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={errorMessage}
      />
    </div>
  );
};

export default CatiAnomalies;
